// Client-side spiegel van energie_service/contracts.py voor live preview tijdens
// contract editing. Krijgt EnergyFlow.year_totals als input — geen netwerk roundtrip
// nodig bij elke field change in de editor.
//
// LET OP: deze logica MOET gelijk blijven met contracts.py. Bij wijziging in business
// logic: backend tests updaten (test_contracts.py) en hier dezelfde change doen.

import type { ContractCost, ContractCostBreakdown, ContractIn, ScenarioParams, YearTotals } from "./types";

export function evaluateContractClient(
  contract: ContractIn & { id?: number },
  yt: YearTotals,
  params: ScenarioParams
): ContractCost {
  // Live preview gebruikt voor transitie het 2026 regime (gunstigste)
  // — server rekent juiste gewogen gemiddelde
  const has_saldering = params.year === 2026 || params.year === "transition_2026_2027";
  const btw = 1 + contract.btw_pct / 100;
  const eb = contract.energiebelasting_eur_per_kwh;
  const inkoop = contract.inkoopvergoeding_eur_per_kwh;

  const saldeerbaar_kwh = has_saldering ? Math.min(yt.export_kwh, yt.import_kwh) : 0;
  const boven_plafond_kwh = has_saldering ? Math.max(0, yt.export_kwh - yt.import_kwh) : yt.export_kwh;

  const vastrecht_eur = contract.vastrecht_eur_per_maand * 12;

  // Mech A
  const netto_belastbaar = Math.max(0, yt.import_kwh - saldeerbaar_kwh);
  const import_belasting_eur = netto_belastbaar * eb * btw;
  const saldering_belasting_korting_eur = saldeerbaar_kwh * eb * btw;

  // Mech B
  let import_marktprijs_eur = 0;
  let saldering_marktprijs_korting_eur = 0;
  let export_opbrengst_marktprijs_eur = 0;
  let boven_plafond_opbrengst_eur = 0;

  if (contract.type === "vast") {
    const kale = contract.kale_prijs_afname_eur_per_kwh ?? 0;
    const tarief = kale + inkoop;
    if (has_saldering) {
      const netto_import = yt.import_kwh - saldeerbaar_kwh;
      import_marktprijs_eur = netto_import * tarief * btw;
      saldering_marktprijs_korting_eur = saldeerbaar_kwh * tarief * btw;
      const teruglv = contract.terugleververgoeding_eur_per_kwh ?? 0;
      boven_plafond_opbrengst_eur = boven_plafond_kwh * teruglv * btw;
    } else {
      import_marktprijs_eur = yt.import_kwh * tarief * btw;
      const min_teruglv = kale * (contract.niet_saldering_pct / 100);
      const teruglv = Math.max(contract.terugleververgoeding_eur_per_kwh ?? 0, min_teruglv);
      boven_plafond_opbrengst_eur = yt.export_kwh * teruglv * btw;
    }
  } else {
    // dynamisch
    const marktprijs_kosten = yt.gewogen_import_marktprijs_eur_per_kwh * yt.import_kwh;
    const inkoop_op_import = inkoop * yt.import_kwh;
    import_marktprijs_eur = (marktprijs_kosten + inkoop_op_import) * btw;
    export_opbrengst_marktprijs_eur = yt.export_eur_revenue_at_market * btw;
  }

  // Inkoopvergoeding op export
  let inkoopvergoeding_export_eur = 0;
  if (contract.inkoopvergoeding_handling === "uitgekeerd") {
    inkoopvergoeding_export_eur = inkoop * yt.export_kwh * btw;
  } else if (contract.inkoopvergoeding_handling === "afgetrokken") {
    inkoopvergoeding_export_eur = -inkoop * yt.export_kwh * btw;
  }

  // Terugleverkosten
  let terugleverkosten_eur = 0;
  if (contract.terugleverkosten_model === "vast") {
    terugleverkosten_eur = contract.terugleverkosten_vast_eur_per_maand * 12;
  } else if (contract.terugleverkosten_model === "per_kwh") {
    terugleverkosten_eur = contract.terugleverkosten_per_kwh * yt.export_kwh;
  }

  // Wettelijke heffingskorting energiebelasting — gecapt op werkelijke belasting
  const HEFFINGSKORTING_MAX = 631.35;
  const heffingskorting_eur = Math.min(HEFFINGSKORTING_MAX, import_belasting_eur);

  const breakdown: ContractCostBreakdown = {
    vastrecht_eur,
    import_marktprijs_eur,
    import_belasting_eur,
    saldering_belasting_korting_eur,
    saldering_marktprijs_korting_eur,
    export_opbrengst_marktprijs_eur,
    inkoopvergoeding_export_eur,
    boven_plafond_opbrengst_eur,
    terugleverkosten_eur,
    heffingskorting_eur,
    boven_plafond_kwh,
  };

  const total_eur =
    vastrecht_eur +
    import_marktprijs_eur +
    import_belasting_eur +
    terugleverkosten_eur -
    export_opbrengst_marktprijs_eur -
    inkoopvergoeding_export_eur -
    boven_plafond_opbrengst_eur -
    heffingskorting_eur;

  return {
    contract_id: contract.id ?? 0,
    contract_name: contract.name,
    total_eur,
    breakdown,
    // Live preview is altijd zonder curtailment — server doet de optimale beslissing.
    curtailment_applied: false,
    curtailment_hours: 0,
    curtailment_savings_eur: 0,
    curtailment_break_even_eur_per_kwh: null,
    vast_export_value_eur_per_kwh: null,
  };
}
